import { useEffect, type ReactNode } from 'react';
import { useRouter } from 'next/router';
import { MEMBER_VIEW_NONCE_STORAGE_KEY, memberViewPageUrl } from './mode';

export function MemberViewPageShell({ children }: { children: ReactNode }) {
  const router = useRouter();
  useEffect(() => {
    function routeAccountLink(event: MouseEvent) {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const anchor = target.closest('a[href]');
      if (!(anchor instanceof HTMLAnchorElement)) return;
      const url = new URL(anchor.href);
      if (url.origin !== window.location.origin) return;
      const memberPath = memberViewPageUrl(url.pathname);
      if (!memberPath) return;
      event.preventDefault();
      event.stopPropagation();
      const destination = `${memberPath}${url.search}${url.hash}`;
      if (anchor.target === '_blank' || event.metaKey || event.ctrlKey)
        window.open(destination, '_blank');
      else window.location.assign(destination);
    }
    document.addEventListener('click', routeAccountLink, true);
    function routeChangeStart(url: string) {
      const parsed = new URL(url, window.location.origin);
      const memberPath = memberViewPageUrl(parsed.pathname);
      const destination = memberPath
        ? `${memberPath}${parsed.search}${parsed.hash}`
        : parsed.pathname.startsWith('/member-view/accounts/')
          ? `${parsed.pathname}${parsed.search}${parsed.hash}`
          : '/member-view';
      const error = new Error(
        'Member view requires a full navigation.',
      ) as Error & {
        cancelled: boolean;
      };
      error.cancelled = true;
      router.events.emit('routeChangeError', error, url, { shallow: false });
      window.location.assign(destination);
      throw error;
    }
    router.events.on('routeChangeStart', routeChangeStart);
    return () => {
      document.removeEventListener('click', routeAccountLink, true);
      router.events.off('routeChangeStart', routeChangeStart);
    };
  }, [router.events]);

  async function endView() {
    window.sessionStorage.removeItem(MEMBER_VIEW_NONCE_STORAGE_KEY);
    try {
      await fetch('/api/member-view/clear', {
        method: 'POST',
        credentials: 'same-origin',
      });
    } finally {
      window.location.assign('/member-view');
    }
  }

  return (
    <>
      <div className="fixed bottom-0 z-[100] flex w-full items-center justify-between bg-navy px-md py-xs text-white">
        <span className="font-semibold">Support member view · Read only</span>
        <button type="button" onClick={() => void endView()}>
          Leave view
        </button>
      </div>
      {children}
    </>
  );
}
